import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { apiEndpoints } from "../config/api";


const AdminLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const locationFrom = location.state?.from;
  const redirectTo =
    typeof locationFrom === "string"
      ? locationFrom
      : locationFrom?.pathname || "/dashboard";
  const requestedResource =
    typeof locationFrom === "string"
      ? locationFrom
      : locationFrom?.pathname || "the requested page";

  const handleLogin = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setErrorMsg("");

    try {
      const response = await fetch(apiEndpoints.admin.login, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          password: password,
        }),
      });

      const data = await response.json();

      if (response.ok && data.token) {
        console.log("Login successful, storing token and navigating to:", redirectTo);
        localStorage.setItem("adminToken", data.token);

        if (data.admin) {
          localStorage.setItem(
            "adminInfo",
            JSON.stringify({
              name: data.admin.name,
              email: data.admin.email,
              role: data.admin.role,
            })
          );
        }

        toast.success("Login successful!");
        navigate(redirectTo, { replace: true });
      } else {
        console.log("Login failed:", data.message);
        setErrorMsg(data.message || "Invalid email or password");
        toast.error(data.message || "Invalid credentials");
      }
    } catch (error) {
      console.error("Login error:", error);
      setErrorMsg("Network error. Please try again.");
      toast.error("Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    toast.info("Please contact system administrator to reset password");
  };

  return (
    <div className="ui-mdc-auth-bg">
      <div className="ui-mdc-auth-noise" aria-hidden="true" />
      <div className="ui-mdc-auth-shell">
        <div className="ui-mdc-brand-panel">
          <div className="ui-mdc-brand-top">
            <span className="ui-mdc-brand-badge">Yala Vibe Admin</span>
            <h1 className="ui-mdc-brand-title">
              Enterprise Command Center for Safari Operations
            </h1>
            <p className="ui-mdc-brand-text">
              Coordinate field teams, manage guest experiences, and safeguard
              financial operations — all from a zero-trust secured control hub.
            </p>
          </div>

        </div>

        <div className="ui-mdc-auth-card">
          <div className="ui-mdc-card-header">
            <span className="ui-mdc-card-badge">Admin Access Only</span>
            <h2 className="ui-mdc-card-title">Authenticate to continue</h2>
            <p className="ui-mdc-card-subtitle">
              Your session is encrypted and monitored. Use assigned enterprise credentials to enter.
            </p>
            {locationFrom && (
              <div className="ui-mdc-card-notice">
                <span className="ui-mdc-card-notice-indicator" aria-hidden="true" />
                <p>
                  Access requires authentication to reach{" "}
                  <span className="ui-mdc-card-notice-highlight">{requestedResource}</span>.
                </p>
              </div>
            )}
          </div>

          {errorMsg && (
            <div className="ui-mdc-alert" role="alert">
              <div className="ui-mdc-alert-indicator" aria-hidden="true" />
              <p className="ui-mdc-alert-text">{errorMsg}</p>
            </div>
          )}

          <form className="ui-mdc-form" onSubmit={handleLogin}>
            <div className="ui-mdc-input-group">
              <label htmlFor="email" className="ui-mdc-input-label">
                Work Email
              </label>
              <div className="ui-mdc-input-wrapper">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="ui-mdc-input-field"
                  placeholder="you@yalasafari.com"
                />
              </div>
            </div>

            <div className="ui-mdc-input-group">
              <label htmlFor="password" className="ui-mdc-input-label">
                Password
              </label>
              <div className="ui-mdc-input-wrapper">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="ui-mdc-input-field"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className="ui-mdc-inline-link"
                >
                  Forgot Password?
                </button>
              </div>
            </div>

            <div className="ui-mdc-form-footer">
              <label htmlFor="remember-me" className="ui-mdc-checkbox">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="ui-mdc-checkbox-control"
                />
                <span className="ui-mdc-checkbox-decor" aria-hidden="true">
                  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor">
                    <polyline points="4 10 8 14 16 6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <span>Remember this device</span>
              </label>
          
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className={`ui-mdc-submit ${isLoading ? "ui-mdc-submit--loading" : ""}`}
            >
              {isLoading ? (
                <>
                  <span className="ui-mdc-spinner" aria-hidden="true" />
                  Validating credentials...
                </>
              ) : (
                <>
                  <svg
                    className="ui-mdc-submit-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 12h6m3 0a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Secure Sign In
                </>
              )}
            </button>
          </form>

          <div className="ui-mdc-compliance-footnote">
            Encrypted via TLS 1.3 · ISO 27001 compliant · Continuous anomaly detection
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;